import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar,
  Download,
  Eye,
  FileText,
  AlertCircle,
  User,
  Sparkles,
  CheckCircle2,
  Clock,
  Building2,
} from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { getApiErrorDetail } from "../../utils/idempotency";
import { toDisplayString } from "../Bank/bankErrorUtils";
import { downloadPayslip, fetchMyPayslipRunOptions, fetchPayslipRunOptions, getPayslipData } from "./payslipApi";
import { usePrefilledPayrollRunId } from "../payroll/payrollWorkflow";
import PayslipDocument from "./PayslipDocument";
import SearchableSelect from "../../components/common/SearchableSelect";
import { useEmployees } from "../../hooks/useEmployees";
import {
  buildSelfEmployeeFromProfile,
  formatEmployeeLabel,
  isEmployeeSelfService,
  resolveEmployeeIdFromProfile,
  sortEmployeesByLabel,
} from "../../components/common/employeeUtils";
import "../../styles/IndianCalendar.css";
import "./PayslipViewer.css";
import BulkPayslips from "./BulkPayslips";
import { hasRole, normalizeRole } from "../../rbac/access";

function runOptionBadge(opt) {
  if (!opt) return null;
  if (opt.payslip_available === false) {
    return { label: "Pending finalization", className: "bg-amber-100 text-amber-900 border border-amber-200" };
  }
  if (opt.lifecycle_status === "locked") {
    return { label: "Finalized", className: "bg-emerald-100 text-emerald-800 border border-emerald-200" };
  }
  if (opt.status === "processed") {
    return { label: "Processed", className: "bg-blue-100 text-blue-800 border border-blue-200" };
  }
  return { label: "In progress", className: "bg-amber-100 text-amber-900 border border-amber-200" };
}

function employeeOption(emp) {
  const name =
    [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim() ||
    emp.display_name ||
    "Unnamed";
  const code = emp.employee_code || "";
  const dept = emp.department?.name || emp.department_name || "";
  return {
    value: emp.employee_id,
    label: name,
    sublabel: [code, dept].filter(Boolean).join(" · "),
    group: dept || "All employees",
    _raw: emp,
  };
}

function statusBadge(opt) {
  if (!opt) return null;
  if (opt.payslip_available === false) {
    return { label: "Pending finalization", className: "psv-status-badge--pending", icon: Clock };
  }
  if (opt.lifecycle_status === "locked") {
    return { label: "Finalized", className: "psv-status-badge--finalized", icon: CheckCircle2 };
  }
  if (opt.status === "processed") {
    return { label: "Processed", className: "psv-status-badge--processed", icon: CheckCircle2 };
  }
  return { label: "In progress", className: "psv-status-badge--pending", icon: Clock };
}

function employeeInitials(user) {
  const first = user?.employee?.first_name?.[0] || "";
  const last = user?.employee?.last_name?.[0] || "";
  return (first + last).toUpperCase() || "ME";
}

function employeeDisplayName(user) {
  if (user?.employee) {
    const name = [user.employee.first_name, user.employee.last_name].filter(Boolean).join(" ");
    return name || user.employee.employee_code || "You";
  }
  return "Your account";
}

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
};

export default function PayslipViewer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, role, principalType } = useContext(AuthContext);
  const normalizedRole = normalizeRole(role);
  const canBulkPayslips = hasRole(normalizedRole, ["admin", "hr"]);
  const activeTab = canBulkPayslips && searchParams.get("tab") === "bulk" ? "bulk" : "individual";
  const isSelfService = isEmployeeSelfService(user, role, principalType);
  const selfEmployeeId = resolveEmployeeIdFromProfile(user);
  const selfEmployee = buildSelfEmployeeFromProfile(user);
  const [runId, setRunId] = useState("");
  const [empId, setEmpId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [runOptions, setRunOptions] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { employees: loadedEmployees, loading: employeesLoading } = useEmployees({
    enabled: !isSelfService,
  });

  usePrefilledPayrollRunId(setRunId);

  useEffect(() => {
    if (!isSelfService || !selfEmployeeId) return;
    if (!empId || String(empId) !== String(selfEmployeeId)) {
      setEmpId(selfEmployeeId);
      setSelectedEmployee(selfEmployee);
    }
  }, [isSelfService, selfEmployeeId, selfEmployee, empId]);

  const loadRunOptions = useCallback(async () => {
    if (isSelfService) {
      if (!selfEmployeeId) {
        setRunOptions([]);
        return;
      }
    } else if (!empId?.trim()) {
      setRunOptions([]);
      return;
    }
    setRunsLoading(true);
    try {
      const rows = isSelfService
        ? await fetchMyPayslipRunOptions()
        : await fetchPayslipRunOptions(empId.trim());
      setRunOptions(rows);
      setRunId((current) => {
        if (current && rows.some((r) => String(r.payroll_run_id) === String(current))) {
          return current;
        }
        const firstAvailable = rows.find((r) => r.payslip_available !== false);
        return firstAvailable
          ? String(firstAvailable.payroll_run_id)
          : rows.length
            ? String(rows[0].payroll_run_id)
            : "";
      });
    } catch (e) {
      setRunOptions([]);
      setError(getApiErrorDetail(e) || e.message);
    } finally {
      setRunsLoading(false);
    }
  }, [empId, isSelfService, selfEmployeeId]);

  useEffect(() => {
    loadRunOptions();
  }, [loadRunOptions]);

  const selectedRun = useMemo(
    () => runOptions.find((r) => String(r.payroll_run_id) === String(runId)),
    [runOptions, runId]
  );

  const runStatus = statusBadge(selectedRun);
  const payslipReady = selectedRun?.payslip_available !== false;
  const canAct = Boolean(runId?.trim() && empId?.trim() && !loading && payslipReady);

  const employeeSummaryLabel = useMemo(() => {
    if (!empId) return null;
    if (isSelfService) return employeeDisplayName(user);
    if (selectedEmployee) return formatEmployeeLabel(selectedEmployee);
    if (data?.employee?.full_name) return data.employee.full_name;
    if (data?.employee_name) return data.employee_name;
    return "Selected";
  }, [empId, isSelfService, user, selectedEmployee, data]);

  const employeeOptions = useMemo(
    () => sortEmployeesByLabel(loadedEmployees).map(employeeOption),
    [loadedEmployees]
  );

  const payMonthOptions = useMemo(
    () =>
      runOptions.map((opt) => ({
        value: opt.payroll_run_id,
        label: opt.month_label || opt.month_key || "Pay period",
        sublabel:
          opt.period_start && opt.period_end
            ? `${opt.period_start} → ${opt.period_end}`
            : "",
        badge: runOptionBadge(opt),
        _raw: opt,
      })),
    [runOptions]
  );

  const payMonthPlaceholder = runsLoading
    ? "Loading months…"
    : !empId
      ? isSelfService
        ? "Loading your profile…"
        : "Select employee first"
      : runOptions.length === 0
        ? "No payroll months found"
        : "Select pay month";

  const loadJson = async () => {
    if (!runId.trim() || !empId.trim()) {
      alert("Select pay month and employee");
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const res = await getPayslipData(empId.trim(), runId.trim());
      setData(res.data);
    } catch (e) {
      setError(getApiErrorDetail(e) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!runId.trim() || !empId.trim()) {
      alert("Select pay month and employee");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await downloadPayslip(runId.trim(), empId.trim());
      const empName = (data?.employee?.full_name || data?.employee_name || "employee")
        .replace(/\s+/g, "_");
      const monthPart = (selectedRun?.month_key || "payslip").replace("-", "_");
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `payslip_${empName}_${monthPart}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorDetail(e) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="psv-root min-h-screen bg-[#eef2f7] py-4 sm:py-5 md:py-6 px-4 sm:px-5 md:px-6">
      <div className={`psv-page ${activeTab === "bulk" ? "psv-page--wide" : ""}`}>
        {/* Hero header */}
        <motion.header {...fadeUp} className="psv-hero">
          <div className="psv-hero-row">
            <div className="psv-hero-main">
              <div className="psv-hero-icon" aria-hidden>
                <FileText size={22} strokeWidth={1.75} />
              </div>
              <div className="psv-hero-text">
                <div className="psv-hero-badge">
                  <Sparkles size={10} />
                  Payroll
                </div>
                <h1 className="psv-hero-title">Payslip</h1>
                <p className="psv-hero-sub">
                  {activeTab === "bulk"
                    ? "Generate and distribute payslips in bulk for thousands of employees. Jobs run in the background — you can continue using the app."
                    : isSelfService
                    ? "Choose a pay month to preview or download your payslip. Only your own payslips are available here."
                    : "Choose the pay month and employee to preview or download a payslip. Pick the month from the list — no payroll run ID needed."}
                </p>
              </div>
            </div>
          </div>
        </motion.header>

        {canBulkPayslips && (
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.02 }}
            className="psv-tabs"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "individual"}
              className={`psv-tab ${activeTab === "individual" ? "psv-tab--active" : ""}`}
              onClick={() => setSearchParams({})}
            >
              Individual Payslip
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "bulk"}
              className={`psv-tab ${activeTab === "bulk" ? "psv-tab--active" : ""}`}
              onClick={() => setSearchParams({ tab: "bulk" })}
            >
              Bulk Payslips
            </button>
          </motion.div>
        )}

        {activeTab === "bulk" ? (
          <BulkPayslips />
        ) : (
          <>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="psv-alert psv-alert--error"
            role="alert"
          >
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <span>{toDisplayString(error, typeof error === "string" ? error : JSON.stringify(error))}</span>
          </motion.div>
        )}

        {/* Selection grid */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.05 }}
          className="psv-grid"
        >
          {/* Form panel */}
          <div className="psv-panel">
            <div className="psv-panel-head">
              <div className="psv-panel-head-icon" aria-hidden>
                <User size={16} />
              </div>
              <div>
                <h2 className="psv-panel-head-title">Select employee &amp; period</h2>
                <p className="psv-panel-head-sub">Required fields marked with *</p>
              </div>
            </div>

            <div className="psv-panel-body">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isSelfService ? (
                  <div className="psv-field">
                    <label className="psv-field-label">
                      <User size={14} />
                      Employee
                    </label>
                    <div className="psv-self-chip">
                      <span className="psv-self-avatar">{employeeInitials(user)}</span>
                      <span>{employeeDisplayName(user)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="psv-field">
                    <SearchableSelect
                      label="Employee"
                      required
                      value={empId}
                      options={employeeOptions}
                      loading={employeesLoading}
                      allowEmpty
                      emptyLabel="Select employee"
                      searchPlaceholder="Search by name, code, or department…"
                      noResultsText="No employees match your search"
                      onChange={(id, opt) => {
                        setEmpId(id);
                        setSelectedEmployee(opt?._raw || null);
                        setRunId("");
                        setData(null);
                      }}
                    />
                  </div>
                )}

                <div className="psv-field">
                  <SearchableSelect
                    label="Pay month"
                    required
                    value={runId}
                    options={payMonthOptions}
                    loading={runsLoading}
                    disabled={!empId || (runOptions.length === 0 && !runsLoading)}
                    allowEmpty
                    emptyLabel={payMonthPlaceholder}
                    searchPlaceholder="Search by month or status…"
                    noResultsText="No pay months match your search"
                    hint={
                      selectedRun
                        ? `Period: ${selectedRun.period_start} → ${selectedRun.period_end}`
                        : ""
                    }
                    onChange={(id) => {
                      setRunId(id);
                      setData(null);
                    }}
                  />
                </div>
              </div>

              <div className="psv-actions">
                <button
                  type="button"
                  onClick={loadJson}
                  disabled={!canAct}
                  className="psv-btn psv-btn--preview"
                >
                  <Eye size={16} />
                  {loading ? "Loading…" : "Preview payslip"}
                </button>
                <button
                  type="button"
                  onClick={download}
                  disabled={!canAct}
                  className="psv-btn psv-btn--download"
                >
                  <Download size={16} />
                  Download PDF
                </button>
              </div>
            </div>
          </div>

          {/* Summary aside */}
          <div className="psv-panel">
            <div className="psv-panel-head">
              <div className="psv-panel-head-icon" aria-hidden>
                <Building2 size={16} />
              </div>
              <div>
                <h2 className="psv-panel-head-title">Selection summary</h2>
                <p className="psv-panel-head-sub">Review before preview or download</p>
              </div>
            </div>

            <div className="psv-panel-body">
              <div className="psv-summary-row">
                <div className={`psv-summary-icon ${empId ? "psv-summary-icon--active" : ""}`}>
                  <User size={16} />
                </div>
                <div>
                  <p className="psv-summary-label">Employee</p>
                  {empId ? (
                    <p className="psv-summary-value">{employeeSummaryLabel}</p>
                  ) : (
                    <p className="psv-summary-empty">Not selected yet</p>
                  )}
                </div>
              </div>

              <div className="psv-summary-row">
                <div className={`psv-summary-icon ${selectedRun ? "psv-summary-icon--active" : ""}`}>
                  <Calendar size={16} />
                </div>
                <div>
                  <p className="psv-summary-label">Pay month</p>
                  {selectedRun ? (
                    <>
                      <p className="psv-summary-value">
                        {selectedRun.month_label || selectedRun.month_key}
                      </p>
                      {runStatus ? (
                        <span className={`psv-status-badge ${runStatus.className}`}>
                          <runStatus.icon size={11} />
                          {runStatus.label}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <p className="psv-summary-empty">
                      {runsLoading ? "Loading…" : "Choose a pay month"}
                    </p>
                  )}
                </div>
              </div>

              <div className="psv-summary-row">
                <div className={`psv-summary-icon ${data ? "psv-summary-icon--active" : ""}`}>
                  <FileText size={16} />
                </div>
                <div>
                  <p className="psv-summary-label">Preview</p>
                  {data ? (
                    <p className="psv-summary-value">Ready — scroll below</p>
                  ) : (
                    <p className="psv-summary-empty">Click &quot;Preview payslip&quot; to load</p>
                  )}
                </div>
              </div>

              <div className="psv-tip">
                <strong>Tip:</strong>{" "}
                {isSelfService
                  ? "Only your own processed payroll months are listed here."
                  : "Processed payroll months appear in the list. Logo and company details are pulled from your organisation profile."}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Preview or empty state */}
        {data ? (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="psv-preview-section"
          >
            <div className="psv-preview-head">
              <div className="psv-preview-head-title">
                <Eye size={16} />
                Payslip preview
              </div>
              <span className="psv-preview-head-hint">
                {selectedRun?.month_label || selectedRun?.month_key || "Current period"}
              </span>
            </div>
            <div className="psv-preview-body">
              <PayslipDocument data={data} />
            </div>
          </motion.section>
        ) : (
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="psv-panel"
          >
            <div className="psv-empty">
              <div className="psv-empty-icon" aria-hidden>
                <FileText size={24} />
              </div>
              <p className="psv-empty-title">No payslip loaded yet</p>
              <p className="psv-empty-sub">
                {isSelfService
                  ? "Select a pay month, then click Preview to see your payslip here."
                  : "Select an employee and pay month, then click Preview to see the payslip here."}
              </p>
            </div>
          </motion.div>
        )}
          </>
        )}
      </div>
    </div>
  );
}
